import type { CopilotProposalSummary } from "@repo/app-types/entities/copilotProposal";
import { ToolType, type ToolOfType } from "@repo/app-types/entities/tool";
import type { ToolProposalData } from "@repo/app-types/entities/toolProposal";
import { appPaths } from "../../app/appPaths.js";
import { buildCopilotProposalLocation } from "../copilot/copilotDeepLinks.js";
import type { CopilotProposalCardContent } from "../copilot/copilotProposalCard.js";

/** A form label for each of a tool type's settings, in form order. */
type ToolSettingLabels<T extends ToolType> = {
  readonly [K in keyof ToolOfType<T>["settings"]]-?: string;
};

/** The setting labels of each type whose settings a proposal can change. */
const settingLabelsByType: {
  readonly [T in ToolType]?: ToolSettingLabels<T>;
} = {
  [ToolType.CODE]: {
    parameters: "Parameters",
    code: "Code",
    allowed_domains: "Allowed domains",
    timeout_ms: "Timeout",
    mock: "Mock response",
  },
  [ToolType.CUSTOM]: {
    method: "HTTP method",
    url: "API endpoint",
    path_parameters: "Path placeholders",
    timeout_ms: "Timeout",
    headers: "Headers",
    query_params: "Query parameters",
    parameters: "Parameters",
    mock: "Mock response",
  },
};

/** What a tool proposal's card in the chat shows and opens. */
export function readToolProposalCardContent(
  proposal: CopilotProposalSummary<"tool">,
): CopilotProposalCardContent {
  const toolLocation = appPaths.agentTool.build({
    toolId: proposal.resource.id,
  });

  return {
    resourceType: "tool",
    operation: "update",
    summary: proposal.data.summary,
    highlights: listToolProposalFields(proposal.data),
    review: {
      kind: "open_editor",
      location: buildCopilotProposalLocation(toolLocation, proposal.id),
    },
    resourceLocation: { pathname: toolLocation },
  };
}

/** The labels of the form fields a tool proposal changes, in form order. */
export function listToolProposalFields(data: ToolProposalData): string[] {
  const settingLabels: Readonly<Record<string, string>> =
    settingLabelsByType[data.tool_type] ?? {};
  const settings: Readonly<Record<string, unknown>> = data.settings ?? {};
  const changedSettingLabels = Object.entries(settingLabels)
    .filter(([key]) => settings[key] !== undefined)
    .map(([, label]) => label);
  return [
    ...(data.name !== undefined ? ["Name"] : []),
    ...(data.description !== undefined ? ["Description"] : []),
    ...changedSettingLabels,
  ];
}
